import React, { useEffect, useRef, useState } from 'react';
import { useReactTable, getCoreRowModel, flexRender, createColumnHelper } from '@tanstack/react-table';
import type { ColumnHelper } from '@tanstack/react-table';
import { Tooltip, Popover } from '@mui/material';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { Input, Pill, Pagination, Tag, Dropdown, MenuItem, MenuHeader } from '@dsi/react-eds';
import { useTheme } from '../../hooks/useTheme';
import { useLoader } from '../../hooks/useLoader';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { PaginatedPipelines, PipelineItem, PipelineStatus, PipelineStep } from '../../const/PipelineTypes';
import { JSX } from 'react/jsx-runtime';
import { capitalized, dateDifference } from './PipelineTableFunctionality';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './PipelineIcons.css';
import api from '../../api/axiosInstance';
import { LOADER_DEBOUNCE_MS } from '../../const/global-constants';
import TestingCompletedDialog from './TestingCompletedDialog';
import ApprovePromoteDialog from '../TestAutomation/ApprovePromoteDialog';
import RappConfigDialog from './RappConfigDialog';
import {
  MenuItemsArr,
  PIPELINE_ACTIONS,
  PipelineActionValue,
  RE_ONBOARD_ACTION_VALUE,
  getMenuItems,
  getStageTooltipTitle,
  getStageStatus
} from './PipelineUtils';
import DeploymentApprovalDialog from './DeploymentApprovalDialog';
import { gnbDeployService } from '../../services/gnbDeployService';
import StageIcon from './stages/StageIcon';
import StageToolTipIcon from './stages/StageToolTipIcon';
import { AxiosError } from 'axios';
import { useDebounce } from '../../hooks/useDebounce';
import { useAuth } from '../../hooks/AuthProvider';
import summaryStyles from './Summary.module.css';
import { workflowManualActionsService } from '../../services/workflowManualActionsService';

const columnHelper: ColumnHelper<PipelineItem> = createColumnHelper<PipelineItem>();
interface PipelineSummaryProps {
  pipelineData: PaginatedPipelines;
  className?: string;
  id?: string;
}
const PipelineTable = ({ pipelineData }: PipelineSummaryProps) => {
  const { theme } = useTheme();
  const { showLoader, hideLoader } = useLoader();
  const { addNotifications } = useSwobNotifications();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(pipelineData.data || []);
  const [completeData, setCompleteData] = useState(pipelineData);

  const [searchInput, setSearchInput] = useState<string>('');
  const globalFilter = useDebounce(searchInput, 300);
  const [statusFilters, setStatusFilters] = useState<string>('');
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: pipelineData.limit
  });
  const [reOnboardPendingFamilies, setReOnboardPendingFamilies] = useState<Set<string>>(new Set());
  const [stagePopover, setStagePopover] = useState<{
    position: { top: number; left: number } | null;
    pipeline: PipelineItem | null;
    stageName: string | null;
  }>({ position: null, pipeline: null, stageName: null });

  const [testingCompletedDialog, setTestingCompletedDialog] = useState<{
    open: boolean;
    pipeline: PipelineItem | null;
    step: PipelineStep | null;
  }>({ open: false, pipeline: null, step: null });

  const [approveAndPromoteDialog, setApproveAndPromoteDialog] = useState<{
    open: boolean;
    onlyReview: boolean;
    pipeline: PipelineItem | null;
    step: PipelineStep | null;
  }>({ open: false, onlyReview: false, pipeline: null, step: null });

  const [rappConfigDialog, setRappConfigDialog] = useState<{
    open: boolean;
    pipeline: PipelineItem | null;
  }>({ open: false, pipeline: null });
  const [deploymentApprovalDialog, setDeploymentApprovalDialog] = useState<{ open: boolean; pipeline: PipelineItem | null }>({
    open: false,
    pipeline: null
  });

  // Sync local state when pipeline data changes (e.g. websocket updates).
  // Re-apply active status filter so rows that no longer match are removed.
  useEffect(() => {
    const filtered = statusFilters ? pipelineData.data.filter((item) => item.status?.toLowerCase() === statusFilters) : pipelineData.data;
    setData(filtered);
    setCompleteData(pipelineData);
  }, [pipelineData, statusFilters]);

  const handleStageIconClick = (event: React.MouseEvent<HTMLElement>, pipeline: PipelineItem, stageName: string) => {
    // remove when bff fixes the stage name
    if (stageName.toLowerCase() === 'artifact onboarding') {
      stageName = 'artifacts onboarding';
    }

    setStagePopover({
      position: { top: event.clientY, left: event.clientX },
      pipeline: pipeline,
      stageName
    });
  };

  // Normalize ticket ids like T-100-prev-1 to T-100 so all re-onboarding variants share one family key.
  const getTicketFamilyKey = (ticketId: string): string => {
    return ticketId.replace(/-prev-\d+$/i, '');
  };

  const isReOnboardPendingForTicket = (ticketId: string): boolean => {
    return reOnboardPendingFamilies.has(getTicketFamilyKey(ticketId));
  };

  const getPageCount = (total: number, limit: number) => {
    if (total % limit === 0) {
      return total / limit;
    } else {
      return Math.ceil(total / limit);
    }
  };

  const fetchPipelines = async (pageIndex: number, pageSize?: number, statusFilter?: string, globalFilter?: string) => {
    try {
      showLoader();
      const size = pageSize || table.getState().pagination.pageSize;
      let baseApi = `/api/workflows/paginated?limit=${size}&offset=${pageIndex * size}`;
      if (statusFilter && statusFilter !== '') baseApi = `${baseApi}&status=${statusFilter}`;
      if (globalFilter && globalFilter !== '') baseApi = `${baseApi}&global=${encodeURIComponent(globalFilter)}`;

      const res = await api.get(baseApi);
      table.setPageSize(res.data.limit);
      table.options.rowCount = res.data.total;
      table.options.pageCount = getPageCount(res.data.total, res.data.limit);
      setCompleteData(res.data);
      setData(res.data.data);
    } catch (error) {
      addNotifications([
        {
          title: 'Error fetching pipelines',
          icon: 'icon-triangle-warning',
          description: error instanceof Error ? error.message : 'An unexpected error occurred',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setTimeout(() => hideLoader(), LOADER_DEBOUNCE_MS);
    }
  };

  const fetchPipelinesAsIs = () => {
    fetchPipelines(pagination.pageIndex, pagination.pageSize, statusFilters, globalFilter);
  };

  const statusGlobalFilter = (statusFilter?: string, searchValue?: string) => {
    if (statusFilter || statusFilter === '') setStatusFilters(statusFilter);
    if (searchValue || searchValue === '') setSearchInput(searchValue);
    table.setPageIndex(0);
    // Only fetch immediately for status changes; search is debounced via useEffect
    if (searchValue === undefined) {
      fetchPipelines(0, undefined, statusFilter, globalFilter);
    }
  };

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    table.setPageIndex(0);
    fetchPipelines(0, undefined, statusFilters, globalFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilter]);

  const handleStagePopoverClose = () => {
    setStagePopover({ position: null, pipeline: null, stageName: null });
  };

  const handleTestingCompletedDialogClose = () => {
    setTestingCompletedDialog({ open: false, pipeline: null, step: null });
  };
  const handleTestingCompleted = async (status: PipelineStatus, pipeline: PipelineItem, step: PipelineStep) => {
    // Refresh the current page data
    await createWorkflowStep(pipeline, step.Stage, step.Step, status);
    if (status === PipelineStatus.Success) {
      promoteArtifacts(pipeline.TicketId, pipeline.automationFlow?.id);
    }
    fetchPipelinesAsIs();
    handleTestingCompletedDialogClose();
  };

  const handleApproveAndPromoteDialogClose = () => {
    setApproveAndPromoteDialog({ open: false, onlyReview: false, pipeline: null, step: null });
  };
  const handleApproveAndPromote = () => {
    // Refresh the current page data
    fetchPipelinesAsIs();
    handleApproveAndPromoteDialogClose();
  };

  const handleStepClick = (pipeline: PipelineItem, step: PipelineStep) => {
    navigate(`/automation-flows/${pipeline.TicketId}/${pipeline.automationFlow.id}`, {
      state: {
        pipeline: pipeline,
        expandStage: step.Stage,
        expandStep: step.Step,
        logsUrl: step.logs,
        initialTab: 'steps' // Indicate that we want to start on steps tab
      }
    });
    handleStagePopoverClose();
  };

  const handleRowClick = (pipeline: PipelineItem) => {
    navigate(`/automation-flows/${pipeline.TicketId}/${pipeline.automationFlow.id}`, {
      state: {
        pipeline: pipeline,
        expandStage: '', // No initial stage expansion
        expandStep: '', // No initial step expansion
        logsUrl: '' // No initial logs URL
      }
    });
  };

  const getStatusStyle = (displayStatus: string) => {
    switch (displayStatus) {
      case PipelineStatus.Success:
      case PipelineStatus.Completed:
        return summaryStyles.statusIconGreen;
      case PipelineStatus.Failure:
        return summaryStyles.statusIconRed;
      case PipelineStatus.InProgress:
        return summaryStyles.statusIconBlue;
      case PipelineStatus.Pending:
        return summaryStyles.statusIconYellow;
      case PipelineStatus.Canceled:
        return summaryStyles.statusIconGray;
      case PipelineStatus.Banned:
        return summaryStyles.statusIconRed;
      case PipelineStatus.Manual:
        return summaryStyles.statusIconYellow;
      case PipelineStatus.Warning:
        return summaryStyles.statusIconOrange;
      case PipelineStatus.Aborted:
        return summaryStyles.statusIconStrongRed;
      case PipelineStatus.Paused:
        return summaryStyles.statusIconLightBlue;
      default:
        return summaryStyles.statusIconBlue;
    }
  };

  const columns = () => [
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => {
        const displayStatus = info.getValue().toLowerCase() as PipelineStatus;
        const steps = info.row.original.steps;
        let duration = '';
        if (steps && steps.length > 0) {
          // Find the first step of the first stage
          const sortedByStage = [...steps].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          const firstStep = sortedByStage[0];
          // Find the last step of the last stage
          const lastStep = sortedByStage[sortedByStage.length - 1];
          const start = new Date(firstStep.timestamp).getTime();
          const end = new Date(lastStep.timestamp).getTime();
          if (!isNaN(start) && !isNaN(end) && end > start) {
            const diff = end - start;
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);
            duration = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
          } else {
            duration = '00:00:00';
          }
        } else {
          duration = '00:00:00';
        }
        return (
          <div>
            <ReactEDSWrapper component={Pill}>
              <span style={{ marginRight: '5px' }} className={`${summaryStyles.statusIcon} ${getStatusStyle(displayStatus)}`}></span>
              {capitalized(displayStatus)}
            </ReactEDSWrapper>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                color: !theme ? '#FFFFFF' : 'inherit'
              }}
            >
              <span>
                {<i className="icon icon-time" style={{ marginRight: '8px' }}></i>}
                {duration}
              </span>
              <span>
                <i className="icon icon-calendar" style={{ marginRight: '8px' }}></i>
                {(() => {
                  const steps = info.row.original.steps;
                  if (steps && steps.length > 0) {
                    // Sort steps by timestamp ascending
                    const sortedSteps = [...steps].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
                    const firstStep = sortedSteps[0];
                    return dateDifference(new Date(firstStep.timestamp), new Date());
                  } else {
                    return dateDifference(new Date(info.row.original.createdAt), new Date());
                  }
                })()}
              </span>
            </div>
          </div>
        );
      }
    }),
    columnHelper.accessor('TicketId', {
      header: 'Automation Workflow',
      cell: (info) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span>{`${info.row.original.automationFlow.name} - ${info.row.original.automationFlow.cardName}`}</span>
          <span
            style={{
              color: '#1174E6',
              cursor: 'pointer'
            }}
            // TODO: handleRowClick directs to the box view, change later
            onClick={() => handleRowClick(info.row.original)}
          >
            {info.getValue()}
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {info.row.original.metadata && (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {info.row.original.status === PipelineStatus.Warning ? (
                    <ReactEDSWrapper component={Tag} color="orange">
                      Approved with Warnings
                    </ReactEDSWrapper>
                  ) : (
                    <></>
                  )}
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {info.row.original.status === PipelineStatus.Manual && (
                    <ReactEDSWrapper component={Tag} color="yellow">
                      Manual Action Required
                    </ReactEDSWrapper>
                  )}
                  {info.row.original.metadata.ProductLine && (
                    <ReactEDSWrapper component={Tag} color="blue">
                      {info.row.original.metadata.ProductLine}
                    </ReactEDSWrapper>
                  )}
                  {info.row.original.metadata.Header && (
                    <ReactEDSWrapper component={Tag} color="purple">
                      {info.row.original.metadata.Header}
                    </ReactEDSWrapper>
                  )}
                </div>
                {!info.row.original.metadata.ProductLine?.toLowerCase().includes('cnis') &&
                  Array.isArray(info.row.original.metadata.Products) &&
                  info.row.original.metadata.Products.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {info.row.original.metadata.Products.map((product, idx: number) => (
                        <ReactEDSWrapper key={idx} component={Tag} color="green">
                          {`${product.ProductNumber} (${product.RState})`}
                        </ReactEDSWrapper>
                      ))}
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
      )
    }),
    columnHelper.accessor('automationFlow', {
      header: 'Environment',
      cell: (info) => {
        const automationFlow = info.getValue();
        const environmentName = automationFlow?.eiapCluster?.name || automationFlow?.cnisEndpoint?.name || 'Unknown';
        return <div style={{ fontWeight: '500' }}>{environmentName}</div>;
      }
    }),
    columnHelper.accessor('steps', {
      header: () => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <span>Stages</span>
        </div>
      ),
      cell: (info) => {
        const pipeline = info.row.original;
        // Use dynamic stages from metadata instead of hardcoded stages
        const stages = info.row.original.metadata?.predefinedStages || [];

        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {stages.map((stageName: string, idx: number, arr: string[]) => {
              const status = getStageStatus(stageName, pipeline);
              const isPending = status === PipelineStatus.Pending;

              return (
                <React.Fragment key={stageName}>
                  <Tooltip
                    title={getStageTooltipTitle(pipeline, stageName, status, isPending)}
                    slotProps={{
                      popper: {
                        sx: {
                          '& .MuiTooltip-tooltip': {
                            backgroundColor: theme ? '#4E4E4E' : '#F2F2F2',
                            color: theme ? '#FFFFFF' : '#242424'
                            // fontSize: 14
                          }
                        }
                      }
                    }}
                  >
                    <span
                      style={{
                        cursor: isPending ? 'default' : 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        opacity: isPending ? 0.7 : 1
                      }}
                      onClick={(e) => !isPending && handleStageIconClick(e, pipeline, stageName)}
                    >
                      <StageIcon status={status} theme={theme} />
                    </span>
                  </Tooltip>
                  {idx < arr.length - 1 && (
                    <span
                      style={{
                        display: 'inline-block',
                        width: 18,
                        height: 2,
                        backgroundColor: '#878787',
                        borderRadius: 1
                      }}
                    />
                  )}
                </React.Fragment>
              );
            })}

            <Popover
              anchorReference="anchorPosition"
              anchorPosition={stagePopover.position ? { top: stagePopover.position.top, left: stagePopover.position.left } : undefined}
              open={Boolean(stagePopover.position) && stagePopover.pipeline?.TicketId === pipeline.TicketId}
              onClose={handleStagePopoverClose}
              PaperProps={{
                style: {
                  padding: 0,
                  borderRadius: 12,
                  boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
                  background: theme ? '#4E4E4E' : '#F2F2F2',
                  color: theme ? '#FFFFFF' : '#242424',
                  overflow: 'visible'
                }
              }}
            >
              {stagePopover.stageName && stagePopover.pipeline && (
                <div style={{ padding: '18px 18px 12px 18px', borderRadius: 12, color: theme ? '#FFFFFF' : '#242424' }}>
                  <div style={{ fontWeight: 500, marginBottom: 18 }}>Stage: {stagePopover.stageName}</div>
                  <div>
                    {(() => {
                      const steps = stagePopover.pipeline.steps.filter(
                        (step) => step.Stage && step.Stage.toLowerCase() === stagePopover.stageName?.toLowerCase()
                      );
                      // Deduplicate by Step (case-insensitive), keeping the latest by timestamp
                      const stepMap = new Map();
                      steps.forEach((step: { Step: string; timestamp: string | number | Date }) => {
                        const key = step.Step.toLowerCase();
                        const existing = stepMap.get(key);
                        if (!existing || new Date(step.timestamp).getTime() > new Date(existing.timestamp).getTime()) {
                          stepMap.set(key, step);
                        }
                      });
                      const uniqueSteps = Array.from(stepMap.values());
                      return uniqueSteps.map((step) =>
                        (() => {
                          const rawStepStatus = step.Status.toLowerCase() as PipelineStatus;
                          const pipelineStatus = stagePopover.pipeline?.status?.toLowerCase() as PipelineStatus;
                          const isArtifactsRetrieving = !!step.Stage && step.Stage.toLowerCase().includes('artifacts retrieving');
                          const displayStepStatus =
                            rawStepStatus === PipelineStatus.Manual && pipelineStatus === PipelineStatus.Canceled && isArtifactsRetrieving
                              ? PipelineStatus.Canceled
                              : rawStepStatus;
                          console.log(displayStepStatus);

                          return (
                            <div
                              key={step.Step}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                marginBottom: 6,
                                cursor: 'pointer',
                                padding: '2px 0',
                                borderRadius: 8,
                                transition: 'background 0.2s'
                              }}
                              onClick={() => stagePopover.pipeline && handleStepClick(stagePopover.pipeline, step)}
                            >
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <StageToolTipIcon status={displayStepStatus} theme={theme} />
                              </div>
                              <span style={{ fontWeight: 400, lineHeight: 1 }}>{step.Step}</span>
                              <span
                                style={{
                                  marginLeft: 'auto',
                                  fontSize: 18,
                                  color: theme ? '#FFFFFF' : '#242424',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <i className="icon icon-launch" />
                              </span>
                            </div>
                          );
                        })()
                      );
                    })()}
                  </div>
                </div>
              )}
            </Popover>
          </div>
        );
      }
    }),
    columnHelper.display({
      id: 'actions',
      header: () => (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {/* <Tooltip title="Coming soon" placement="top"> */}
          <span>Actions</span>
          {/* </Tooltip> */}
        </div>
      ),
      cell: (info) => {
        return (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <ReactEDSWrapper className="wf-con-menu" component={Dropdown} more>
              {getMenuItems(info.row.original).map((menuItem: MenuItemsArr) => (
                <React.Fragment key={menuItem.label}>
                  {menuItem.separator && <hr />}
                  {menuItem.header ? (
                    <ReactEDSWrapper component={MenuHeader}>{menuItem.label}</ReactEDSWrapper>
                  ) : (
                    (() => {
                      const isPendingReOnboard =
                        menuItem.value === RE_ONBOARD_ACTION_VALUE && isReOnboardPendingForTicket(info.row.original.TicketId);
                      const pendingMessage = 'Re-onboarding request already in progress for this ticket family.';

                      return (
                        <Tooltip title={isPendingReOnboard ? pendingMessage : ''} placement="left">
                          <span style={{ display: 'block' }}>
                            <ReactEDSWrapper
                              component={MenuItem}
                              onClick={() => {
                                if (isPendingReOnboard) {
                                  return;
                                }
                                if (menuItem.value && menuItem.step) handleManualActions(info.row.original, menuItem.value, menuItem.step);
                              }}
                              disabled={menuItem.disabled || isPendingReOnboard}
                            >
                              {menuItem.label}
                            </ReactEDSWrapper>
                          </span>
                        </Tooltip>
                      );
                    })()
                  )}
                </React.Fragment>
              ))}
            </ReactEDSWrapper>
          </div>
        );
      }
    })
  ];

  const handleSelectiveOnboardingAction = async (pipeline: PipelineItem, mode: 'approve' | 'reject') => {
    showLoader();
    const successNotification =
      mode === 'approve'
        ? {
            title: 'Selective Onboarding approved',
            icon: 'icon-success',
            description: `Ticket ${pipeline.TicketId} approved at Artifacts Retrieving.`,
            timestamp: new Date(),
            color: 'green'
          }
        : {
            title: 'Selective Onboarding rejected',
            icon: 'icon-block',
            description: `Ticket ${pipeline.TicketId} was rejected at Artifacts Retrieving.`,
            timestamp: new Date(),
            color: 'orange'
          };

    const errorFallback =
      mode === 'approve'
        ? 'An error occurred while approving selective onboarding.'
        : 'An error occurred while rejecting selective onboarding.';

    try {
      await workflowManualActionsService.sendHandleSelectiveOnboardingAction(pipeline, mode);
      addNotifications([successNotification]);
      fetchPipelinesAsIs();
    } catch (error) {
      console.error(error);
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: error && error instanceof AxiosError ? error.response?.data?.message || error.message : errorFallback,
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setTimeout(() => {
        hideLoader();
      }, LOADER_DEBOUNCE_MS);
    }
  };

  const handleDeploymentApprove = async (pipeline: PipelineItem) => {
    showLoader();
    try {
      await gnbDeployService.approve({
        ticketId: pipeline.TicketId,
        automationFlowId: pipeline.automationFlow?.id || '',
        approve: true
      });
      addNotifications([
        {
          title: 'Deployment Approved',
          icon: 'icon-success',
          description: `Deployment approved for ${pipeline.TicketId}.`,
          timestamp: new Date(),
          color: 'green'
        }
      ]);
      fetchPipelinesAsIs();
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: error instanceof AxiosError ? error.response?.data?.message || error.message : 'Failed to approve deployment.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setTimeout(() => {
        hideLoader();
      }, LOADER_DEBOUNCE_MS);
    }
  };

  const handleDeploymentReject = async (pipeline: PipelineItem, reason: string) => {
    showLoader();
    try {
      await gnbDeployService.approve({
        ticketId: pipeline.TicketId,
        automationFlowId: pipeline.automationFlow?.id || '',
        approve: false,
        reason
      });
      addNotifications([
        {
          title: 'Deployment Rejected',
          icon: 'icon-block',
          description: `Deployment rejected for ${pipeline.TicketId}.`,
          timestamp: new Date(),
          color: 'orange'
        }
      ]);
      fetchPipelinesAsIs();
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: error instanceof AxiosError ? error.response?.data?.message || error.message : 'Failed to reject deployment.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setTimeout(() => {
        hideLoader();
      }, LOADER_DEBOUNCE_MS);
    }
  };

  const handleReOnboardAction = async (pipeline: PipelineItem) => {
    if (!pipeline.automationFlow?.id) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: `Automation Workflow id is missing for ticket ${pipeline.TicketId}.`,
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      return;
    }

    const familyKey = getTicketFamilyKey(pipeline.TicketId);
    if (reOnboardPendingFamilies.has(familyKey)) {
      return;
    }
    showLoader();
    setReOnboardPendingFamilies((prev) => {
      const next = new Set(prev);
      next.add(familyKey);
      return next;
    });

    try {
      const response = await api.post('/api/workflows-onboarding/re-onboard', {
        sourceTicketId: pipeline.TicketId,
        automationFlowId: pipeline.automationFlow.id,
        triggeredBy: user?.username || ''
      });

      addNotifications([
        {
          title: 'Re-onboarding accepted',
          icon: 'icon-success',
          description: response?.data?.message || `Re-onboarding request accepted for ticket ${pipeline.TicketId}.`,
          timestamp: new Date(),
          color: 'green'
        }
      ]);
      fetchPipelinesAsIs();
    } catch (error) {
      console.error(error);
      const axiosError = error instanceof AxiosError ? error : null;
      const statusCode = axiosError?.response?.status;
      const backendMessage = axiosError?.response?.data?.message || axiosError?.message;
      const errorInfoByStatus: Record<number, { title: string; defaultDescription: string }> = {
        400: {
          title: 'Invalid re-onboarding request',
          defaultDescription: 'Request is invalid or ticket status is not eligible for re-onboarding.'
        },
        404: {
          title: 'Ticket not found',
          defaultDescription: `Source ticket ${pipeline.TicketId} was not found.`
        },
        409: {
          title: 'Re-onboarding blocked',
          defaultDescription:
            'The latest run in the same ticket family is still active. Information may be outdated; please refresh to see updated status.'
        },
        502: {
          title: 'Re-onboarding trigger failed',
          defaultDescription: 'Downstream trigger failed. Please retry.'
        }
      };

      const errorInfo = statusCode ? errorInfoByStatus[statusCode] : undefined;
      const title = errorInfo?.title || 'Error';
      const description = backendMessage || errorInfo?.defaultDescription || 'An error occurred while triggering re-onboarding.';

      addNotifications([
        {
          title,
          icon: 'icon-triangle-warning',
          description,
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setReOnboardPendingFamilies((prev) => {
        const next = new Set(prev);
        next.delete(familyKey);
        return next;
      });
      setTimeout(() => {
        hideLoader();
      }, LOADER_DEBOUNCE_MS);
    }
  };

  const handleManualActions = async (pipeline: PipelineItem, action: PipelineActionValue, step: PipelineStep) => {
    if (action === PIPELINE_ACTIONS.DeploymentCompleted) {
      if (pipeline?.metadata?.ProductLine?.toLowerCase().includes('cloud ran')) {
        // Cloud RAN: Start automatic test execution
        if (pipeline?.automationFlow?.configuration?.testAutomation === true) {
          const res = await automationTestExecution(pipeline);
          if (res === 'ok') {
            await createWorkflowStep(pipeline, step.Stage, step.Step, PipelineStatus.Success);
          }
        } else {
          await createWorkflowStep(pipeline, 'Testing', 'Testing', PipelineStatus.Manual);
        }
        fetchPipelinesAsIs();
      } else if (pipeline?.metadata?.ProductLine?.toLowerCase().includes('baseband radio node')) {
        // Purpose-Built: Create "Testing" step with Manual status
        // tells the backend to set Testing stage to Manual
        await createWorkflowStep(pipeline, step.Stage, step.Step, PipelineStatus.Success);
        if (pipeline?.automationFlow?.configuration?.testAutomation === false) {
          await createWorkflowStep(pipeline, 'Testing', 'Testing', PipelineStatus.Manual);
        }
        fetchPipelinesAsIs();
      } else if (pipeline?.metadata?.ProductLine?.toLowerCase().includes('rapp')) {
        await deployRAppManually(pipeline);
      }
    }
    if (action === PIPELINE_ACTIONS.RappConfigure) {
      setRappConfigDialog({ open: true, pipeline });
    }
    if (action === PIPELINE_ACTIONS.SelectiveOnboardingApprove) {
      await handleSelectiveOnboardingAction(pipeline, 'approve');
    }
    if (action === PIPELINE_ACTIONS.SelectiveOnboardingReject) {
      await handleSelectiveOnboardingAction(pipeline, 'reject');
    }
    if (action === RE_ONBOARD_ACTION_VALUE) {
      await handleReOnboardAction(pipeline);
    }
    if (action === PIPELINE_ACTIONS.ApproveAndPromote) {
      setApproveAndPromoteDialog({
        open: true,
        onlyReview: false,
        pipeline: pipeline,
        step: step
      });
    }
    if (action === PIPELINE_ACTIONS.ReviewTestExecutions) {
      setApproveAndPromoteDialog({
        open: true,
        onlyReview: true,
        pipeline: pipeline,
        step: step
      });
    }
    if (action === PIPELINE_ACTIONS.TestingCompleted) {
      setTestingCompletedDialog({
        open: true,
        pipeline: pipeline,
        step: step
      });
    }
    if (action === PIPELINE_ACTIONS.DeployCrGnb) {
      navigate(`/automation-flows/${pipeline.TicketId}/${pipeline.id}`, { state: { pipeline, initialTab: 'deployment' } });
    }
    if (action === PIPELINE_ACTIONS.DeploymentApprove) {
      setDeploymentApprovalDialog({ open: true, pipeline });
    }
    if (action === PIPELINE_ACTIONS.Abort) {
      abortWorkflow(pipeline);
    }
    if (action === PIPELINE_ACTIONS.Pause) {
      pauseWorkflow(pipeline);
    }
    if (action === PIPELINE_ACTIONS.Cancel) {
      cancelWorkflow(pipeline);
    }
    if (action === PIPELINE_ACTIONS.Resume) {
      resumeWorkflow(pipeline);
    }
    if (action === PIPELINE_ACTIONS.Retry) {
      retryWorkflow(pipeline);
    }
  };

  const promoteArtifacts = async (ticketId: string, automationFlowId: string): Promise<string> => {
    showLoader();
    try {
      await workflowManualActionsService.sendPromoteArtifacts(ticketId, automationFlowId);
      addNotifications([
        {
          title: 'Success',
          icon: 'icon-success',
          description: `Artifacts promoted for ${ticketId} Successfully.`,
          timestamp: new Date(),
          color: 'green'
        }
      ]);
      return 'ok';
    } catch (error) {
      console.error(error);
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while promoting artifacts',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      return 'nok';
    } finally {
      setTimeout(() => {
        hideLoader();
      }, LOADER_DEBOUNCE_MS);
    }
  };
  const createWorkflowStep = async (pipeline: PipelineItem, stage: string, step: string, status: PipelineStatus): Promise<string> => {
    showLoader();
    try {
      return await workflowManualActionsService.sendCreateWorkflowStep(pipeline, stage, step, status);
    } catch (error) {
      console.error(error);
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while creating step for workflow',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      hideLoader();
      throw error; // Re-throw to allow caller to handle
    } finally {
      setTimeout(() => {
        hideLoader();
      }, LOADER_DEBOUNCE_MS);
    }
  };

  const automationTestExecution = async (pipeline: PipelineItem): Promise<string> => {
    showLoader();
    try {
      return await workflowManualActionsService.sendAutomationTestExecution(pipeline);
    } catch (error) {
      console.error(error);
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while creating step for workflow',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      return 'nok';
    } finally {
      setTimeout(() => {
        hideLoader();
      }, LOADER_DEBOUNCE_MS);
    }
  };

  const abortWorkflow = async (pipeline: PipelineItem) => {
    return await sendWorkflowAction(pipeline, 'abort');
  };

  const pauseWorkflow = async (pipeline: PipelineItem) => {
    return await sendWorkflowAction(pipeline, 'pause');
  };

  const cancelWorkflow = async (pipeline: PipelineItem) => {
    return await sendWorkflowAction(pipeline, 'cancel');
  };

  const resumeWorkflow = async (pipeline: PipelineItem) => {
    return await sendWorkflowAction(pipeline, 'resume');
  };

  const retryWorkflow = async (pipeline: PipelineItem) => {
    return await sendWorkflowAction(pipeline, 'retry');
  };

  const sendWorkflowAction = async (pipeline: PipelineItem, endpoint: string) => {
    showLoader();
    try {
      return await workflowManualActionsService.sendWorkflowAction(pipeline, endpoint);
    } catch (error) {
      console.error(error);
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while pausing workflow',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      return 'nok';
    } finally {
      setTimeout(() => hideLoader(), LOADER_DEBOUNCE_MS);
    }
  };

  const deployRAppManually = async (pipeline: PipelineItem) => {
    showLoader();
    try {
      return await workflowManualActionsService.sendDeployRAppManually(pipeline);
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while manually deploying rApp workflow',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      return 'nok';
    } finally {
      setTimeout(() => {
        hideLoader();
      }, LOADER_DEBOUNCE_MS);
    }
  };

  const table = useReactTable({
    data,
    columns: columns(),
    onPaginationChange: setPagination,
    manualPagination: true,
    rowCount: pipelineData.total,
    getCoreRowModel: getCoreRowModel(),
    /*debugTable: true,
    debugHeaders: true,
    debugColumns: false,*/
    state: {
      //...
      pagination
    }
  });

  let paginationButtons = [];
  const paginationGoups: JSX.Element[][] = [];
  const paginationGoupLimit = 10;
  const currentPage = table.getState().pagination.pageIndex;
  for (let i = 0; i < table.getPageCount(); i++) {
    if (i > 0 && i % paginationGoupLimit === 0) {
      paginationButtons.push(<span>...</span>);
      paginationGoups.push(paginationButtons);
      paginationButtons = [];
    }
    paginationButtons.push(
      <button className={currentPage === i ? 'active' : ''} key={i} onClick={() => table.setPageIndex(i)}>
        {i + 1}
      </button>
    );
  }
  if (paginationButtons.length > 0) {
    if (paginationGoups.length > 0) {
      paginationButtons.unshift(<span>...</span>);
    }
    paginationGoups.push(paginationButtons);
    paginationButtons = [];
  }

  const pillStatusFiltering = (id: string, status: string) => {
    let statusRecord: string[] = (table.getColumn('status')?.getFilterValue() as string[]) || [];
    const pillGroup = document.getElementById('pipelinePillGroup');
    if (
      pillGroup?.querySelector(id)?.getAttribute('class') === 'pill toggle' ||
      pillGroup?.querySelector(id)?.getAttribute('class') === 'pill toggle unselected'
    ) {
      statusRecord.push(status);
      table.getColumn('status')?.setFilterValue(statusRecord);
      pillGroup?.querySelector(id)?.setAttribute('class', 'pill toggle selected');
      const nodes = pillGroup?.querySelectorAll('span.pill.toggle');
      nodes?.forEach((node) => {
        if (node.className !== 'pill toggle selected') {
          node.setAttribute('class', 'pill toggle unselected');
        }
      });
    } else {
      statusRecord = statusRecord.filter((item) => item !== status);
      table.getColumn('status')?.setFilterValue(statusRecord);
      const nodes = pillGroup?.querySelectorAll('span.pill.toggle.unselected') || [];
      if (nodes.length === 5) {
        nodes?.forEach((node) => {
          node.setAttribute('class', 'pill toggle');
        });
        pillGroup?.querySelector(id)?.setAttribute('class', 'pill toggle');
      } else {
        pillGroup?.querySelector(id)?.setAttribute('class', 'pill toggle unselected');
      }
    }
    statusGlobalFilter(statusRecord.toString());
  };

  useEffect(() => {
    const status = searchParams.get('status');
    const pill = {
      success: '#pillSuccess',
      failure: '#pillFail',
      warning: '#pillWarning',
      'in-progress': '#pillRun',
      manual: '#pillManual'
    }[status || ''];
    if (pill) pillStatusFiltering(pill, status!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ padding: '16px' }}>
      <div id="pipelinePillGroup" style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <ReactEDSWrapper
          component={Input}
          style={{
            width: '30%'
          }}
          placeholder="Search..."
          icon="search"
          onChange={(e) => {
            setSearchInput(String((e.target as HTMLInputElement).value));
          }}
        />
        <span style={{ color: '#878787', marginLeft: '16px' }}>
          {completeData.total > completeData.limit ? completeData.data.length + completeData.offset : completeData.total} of{' '}
          {completeData.total} items
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '16px' }}>
          <ReactEDSWrapper
            id="pillRun"
            className="toggle"
            component={Pill}
            onClick={() => {
              pillStatusFiltering('#pillRun', 'in-progress');
            }}
          >
            <span
              style={{ marginRight: '5px' }}
              className={`${summaryStyles.statusIcon} ${getStatusStyle(PipelineStatus.InProgress)}`}
            ></span>{' '}
            Running
          </ReactEDSWrapper>
          <ReactEDSWrapper
            id="pillSuccess"
            className="toggle"
            component={Pill}
            onClick={() => {
              pillStatusFiltering('#pillSuccess', 'success');
            }}
          >
            <span style={{ marginRight: '5px' }} className={`${summaryStyles.statusIcon} ${getStatusStyle(PipelineStatus.Success)}`}></span>{' '}
            Successful
          </ReactEDSWrapper>
          <ReactEDSWrapper
            id="pillFail"
            className="toggle"
            component={Pill}
            onClick={() => {
              pillStatusFiltering('#pillFail', 'failure');
            }}
          >
            <span style={{ marginRight: '5px' }} className={`${summaryStyles.statusIcon} ${getStatusStyle(PipelineStatus.Failure)}`}></span>{' '}
            Failed
          </ReactEDSWrapper>
          <ReactEDSWrapper
            id="pillManual"
            className="toggle"
            component={Pill}
            onClick={() => {
              pillStatusFiltering('#pillManual', 'manual');
            }}
          >
            <span style={{ marginRight: '5px' }} className={`${summaryStyles.statusIcon} ${getStatusStyle(PipelineStatus.Manual)}`}></span>{' '}
            Manual
          </ReactEDSWrapper>
          <ReactEDSWrapper
            id="pillWarning"
            className="toggle"
            component={Pill}
            onClick={() => {
              pillStatusFiltering('#pillWarning', 'warning');
            }}
          >
            <span style={{ marginRight: '5px' }} className={`${summaryStyles.statusIcon} ${getStatusStyle(PipelineStatus.Warning)}`}></span>{' '}
            Warning
          </ReactEDSWrapper>
          <ReactEDSWrapper
            id="pillStop"
            className="toggle"
            component={Pill}
            onClick={() => {
              pillStatusFiltering('#pillStop', 'canceled');
            }}
          >
            <span
              style={{ marginRight: '5px' }}
              className={`${summaryStyles.statusIcon} ${getStatusStyle(PipelineStatus.Canceled)}`}
            ></span>{' '}
            Canceled
          </ReactEDSWrapper>
          <ReactEDSWrapper
            id="pillAborted"
            className="toggle"
            component={Pill}
            onClick={() => {
              pillStatusFiltering('#pillAborted', 'aborted');
            }}
          >
            <span style={{ marginRight: '5px' }} className={`${summaryStyles.statusIcon} ${getStatusStyle(PipelineStatus.Aborted)}`}></span>{' '}
            Aborted
          </ReactEDSWrapper>
          <ReactEDSWrapper
            id="pillPaused"
            className="toggle"
            component={Pill}
            onClick={() => {
              pillStatusFiltering('#pillPaused', 'paused');
            }}
          >
            <span style={{ marginRight: '5px' }} className={`${summaryStyles.statusIcon} ${getStatusStyle(PipelineStatus.Paused)}`}></span>{' '}
            Paused
          </ReactEDSWrapper>
        </div>
        {document.querySelectorAll('span.pill.toggle.unselected').length < 8 &&
        document.querySelectorAll('span.pill.toggle.unselected').length != 0 ? (
          <a
            style={{ marginLeft: '5px' }}
            className="toggle-all"
            onClick={() => {
              table.getColumn('status')?.setFilterValue([]);
              const pillGroup = document.getElementById('pipelinePillGroup');

              if (pillGroup) {
                pillGroup.querySelector('#pillStop')?.setAttribute('class', 'pill toggle');
                pillGroup.querySelector('#pillRun')?.setAttribute('class', 'pill toggle');
                pillGroup.querySelector('#pillFail')?.setAttribute('class', 'pill toggle');
                pillGroup.querySelector('#pillSuccess')?.setAttribute('class', 'pill toggle');
                pillGroup.querySelector('#pillManual')?.setAttribute('class', 'pill toggle');
                pillGroup.querySelector('#pillWarning')?.setAttribute('class', 'pill toggle');
                pillGroup.querySelector('#pillAborted')?.setAttribute('class', 'pill toggle');
                pillGroup.querySelector('#pillPaused')?.setAttribute('class', 'pill toggle');
              }
              statusGlobalFilter('');
            }}
          >
            Select all
          </a>
        ) : (
          <></>
        )}
      </div>

      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse'
        }}
      >
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  style={{
                    textAlign: 'left',
                    padding: '12px 16px'
                  }}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getPaginationRowModel().rows.map((row) => (
            <tr
              key={row.id}
              style={{
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = !theme ? '#404040' : '#DCDCDC';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '';
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  style={{
                    padding: '16px',
                    borderTop: `1px solid ${!theme ? '#404040' : '#242424'}`
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      <ReactEDSWrapper
        component={Pagination}
        numPages={table.getPageCount()}
        selectedPage={table.getState().pagination.pageIndex + 1}
        onPaginationChangePage={(page: number) => {
          table.setPageIndex(page - 1);
          fetchPipelines(page - 1, undefined, statusFilters, globalFilter);
        }}
        optionsList={[
          { value: '20', label: '20', active: table.getState().pagination.pageSize === 20 },
          { value: '30', label: '30', active: table.getState().pagination.pageSize === 30 },
          { value: '40', label: '40', active: table.getState().pagination.pageSize === 40 },
          { value: '50', label: '50', active: table.getState().pagination.pageSize === 50 }
        ]}
        onPaginationChangeSelect={(size: string) => {
          table.setPageSize(Number(size));
          table.setPageIndex(0);
          fetchPipelines(0, Number(size), statusFilters, globalFilter);
        }}
        withActions
      />

      <TestingCompletedDialog
        open={testingCompletedDialog.open}
        pipeline={testingCompletedDialog.pipeline}
        step={testingCompletedDialog.step}
        onClose={handleTestingCompletedDialogClose}
        onStatusSubmit={handleTestingCompleted}
      />

      {approveAndPromoteDialog.open && (
        <ApprovePromoteDialog
          open={approveAndPromoteDialog.open}
          onlyReview={approveAndPromoteDialog.onlyReview}
          onClose={handleApproveAndPromoteDialogClose}
          onApproveAndPromote={handleApproveAndPromote}
          pipeline={approveAndPromoteDialog.pipeline}
        />
      )}

      {rappConfigDialog.open && rappConfigDialog.pipeline && (
        <RappConfigDialog
          open={rappConfigDialog.open}
          pipeline={rappConfigDialog.pipeline}
          onClose={() => {
            setRappConfigDialog({ open: false, pipeline: null });
            fetchPipelinesAsIs();
          }}
        />
      )}
      {deploymentApprovalDialog.open && deploymentApprovalDialog.pipeline && (
        <DeploymentApprovalDialog
          open={deploymentApprovalDialog.open}
          onClose={() => setDeploymentApprovalDialog({ open: false, pipeline: null })}
          onApprove={handleDeploymentApprove}
          onReject={handleDeploymentReject}
          pipeline={deploymentApprovalDialog.pipeline}
        />
      )}
    </div>
  );
};

export default PipelineTable;
